import { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import { useQuery } from '@apollo/client';
import { getEstimatedCryptoAmount } from '@libs/helpers';

import { useApiResponse } from '@libs/hooks';
import { CurrencyType, PartialAsset } from '@libs/types';
import { Typography } from '@libs/components';

import {
  getExchangeSettingsV2,
  GetExchangeSettingsV2Response,
  NestedAssetNew,
} from 'src/gql';
import { getAlternativeAssetLabel } from 'src/helpers';

import { ExchangeBalanceMap, ExchangeCurrencyOption } from 'src/types';
import { useExchangeBalanceGe } from './useExchangeBalanceGe';

const sortByDescendingOrder = (
  a: ExchangeCurrencyOption,
  b: ExchangeCurrencyOption
) => b.balance - a.balance;

const mapExchangeCurrency = (
  item: NestedAssetNew,
  isBaseAsset = true,
  balances?: ExchangeBalanceMap
): ExchangeCurrencyOption => {
  const itemBalances = balances?.[item.code];
  const roundedAssetAmount =
    itemBalances?.amount !== undefined
      ? getEstimatedCryptoAmount(
          itemBalances.amount,
          itemBalances.price,
          item.roundingPrecision
        )
      : '0';

  return {
    value: item.code,
    label: item.name,
    description: getAlternativeAssetLabel(item.code as CurrencyType),
    icon: item.iconUrl,
    pairedAssets: item.pairedAssets,
    isBaseAsset,
    name: item.name,
    roundingPrecision: item.roundingPrecision,
    balance: itemBalances?.value || 0,
    isStable: item.isStable,
    isCrypto: item.isCrypto,
    rightItem: (
      <DetailsBalanceWrapper>
        <Typography type="subHeadline" fontFamily="primaryMedium">
          {roundedAssetAmount}{' '}
          {getAlternativeAssetLabel(item.code as CurrencyType)}
        </Typography>
        <CustomTypography type="caption" fontFamily="primaryMedium">
          {itemBalances?.value
            ? `${getEstimatedCryptoAmount(
                itemBalances.value,
                itemBalances.price,
                item.roundingPrecision
              )} ${CurrencyType.USD}`
            : undefined}
        </CustomTypography>
      </DetailsBalanceWrapper>
    ),
    orderingPriority: 0,
  };
};

const PREFERRED_TRADE_ASSET = CurrencyType.USDT;
const getExchangeIntersection = (
  primaryAssets: PartialAsset[],
  secondaryAssets: NestedAssetNew[],
  selectedOpposingAsset: ExchangeCurrencyOption,
  isBaseAsset = true
) => {
  let preferredAsset: ExchangeCurrencyOption | undefined;
  const intersection = primaryAssets.reduce<ExchangeCurrencyOption[]>(
    (acc, curr) => {
      const foundAsset = secondaryAssets.find(
        (asset) => asset.code === curr.code
      );
      if (foundAsset) {
        const mappedAsset = mapExchangeCurrency(foundAsset, isBaseAsset);
        if (foundAsset.code === PREFERRED_TRADE_ASSET) {
          preferredAsset === mappedAsset;
          return [mappedAsset, ...acc];
        }
        return [...acc, mappedAsset];
      }
      return acc;
    },
    []
  );
  const isSelectedAssetInPairedAssets = intersection.find(
    (asset) => asset.value === selectedOpposingAsset?.value
  );
  if (isSelectedAssetInPairedAssets) {
    return [isSelectedAssetInPairedAssets, ...intersection];
  }
  if (preferredAsset) {
    return [preferredAsset, ...intersection];
  }

  return intersection;
};

const cryptoAssetOptions = (
  response: GetExchangeSettingsV2Response,
  assetCodeBalanceMap: ExchangeBalanceMap
) => {
  if (!response) return [];
  return response?.getExchangeSettingsV2?.[0].pairedAssets?.reduce(
    (acc: ExchangeCurrencyOption[], val) => {
      const mapped = mapExchangeCurrency(val, true, assetCodeBalanceMap);

      if (mapped.isCrypto) {
        return [...acc, mapped];
      }

      return acc;
    },
    []
  );
};

export const useExchangeOptionsGe = (assetCode?: CurrencyType) => {
  const [fromAssets, setFromAssets] = useState<ExchangeCurrencyOption[]>([]);
  const [toAssets, setToAssets] = useState<ExchangeCurrencyOption[]>([]);
  const res = useQuery(getExchangeSettingsV2, {
    variables: { record: { assetCode: assetCode } },
  });
  const assetCodeBalanceMap = useExchangeBalanceGe();

  const { response } = useApiResponse(res);

  useEffect(() => {
    if (response) {
      if (assetCode) {
        const assetOptions = cryptoAssetOptions(response, assetCodeBalanceMap);

        const sortedAssetOptions = assetOptions?.sort(sortByDescendingOrder);
        setFromAssets(sortedAssetOptions || []);
        setToAssets(sortedAssetOptions || []);
      } else {
        const assetOptions = response.getExchangeSettingsV2.map((val) =>
          mapExchangeCurrency(val, true, assetCodeBalanceMap)
        );

        const sortedAssetOptions = assetOptions.sort(sortByDescendingOrder);
        setFromAssets(sortedAssetOptions);
        setToAssets(sortedAssetOptions);
      }
    }
  }, [assetCode, assetCodeBalanceMap, response]);

  const allAssets = useMemo(() => {
    if (!response?.getExchangeSettingsV2) return {};

    const assetOptions = response.getExchangeSettingsV2.map((val) =>
      mapExchangeCurrency(val, true, assetCodeBalanceMap)
    );

    return assetOptions.reduce<Record<string, ExchangeCurrencyOption>>(
      (acc, res) => {
        acc[res.value] = res;
        return acc;
      },
      {}
    );
  }, [assetCodeBalanceMap, response]);

  const getPairedAssets = useCallback(
    (val: string) => {
      return (
        allAssets?.[val].pairedAssets
          ?.map((val) =>
            mapExchangeCurrency(
              val as NestedAssetNew,
              false,
              assetCodeBalanceMap
            )
          )
          .sort(sortByDescendingOrder) || []
      );
    },
    [assetCodeBalanceMap, allAssets]
  );

  const getOppositeAssets = (
    asset: ExchangeCurrencyOption,
    opposingAsset: ExchangeCurrencyOption
  ) => {
    if (!asset.pairedAssets || !response?.getExchangeSettingsV2) return;

    if (asset.isBaseAsset) {
      const newQuoteAssets = getExchangeIntersection(
        asset.pairedAssets,
        response.getExchangeSettingsV2,
        opposingAsset,
        true
      );

      return newQuoteAssets;
    } else {
      const newBaseAssets = getExchangeIntersection(
        asset.pairedAssets,
        response.getExchangeSettingsV2,
        opposingAsset,
        false
      );

      return newBaseAssets;
    }
  };

  return {
    fromAssets: fromAssets,
    toAssets: toAssets,
    setFromAssets,
    setToAssets,
    getOppositeAssets,
    getPairedAssets,
    allAssets,
  };
};

const DetailsBalanceWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;
export const CustomTypography = styled(Typography)`
  color: ${({ theme }) => theme.colors.text.secondary};
`;
